import useModal from '@ctn/ui/components/ctn-modal/hooks/useModal';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { Controller, useFormContext } from 'react-hook-form';

import DocumentosListado from '@/components/DocumentosListado';
import EntityGroup from '@/components/EntityGroup';
import ModalAddDocumento from '@/components/ModalAddDocumento';
import { useGlobalContext } from '@/context/global.context';

import { notificationsConfig } from '@/helpers/notifications';
import { type IDocumentoForm, type IDocumentoFormGroup } from '@/interfaces/model/don.interface';
import { findID } from '@/utils/id';

type DocumentGroupProps = {
	readonly idSolicitud: number;
	readonly className?: string;
	readonly allDocuments: IDocumentoForm[];
	readonly name: string;
};

type Form = Record<string, IDocumentoFormGroup>;

const DocumentGroup = ({ idSolicitud, allDocuments, name, className }: DocumentGroupProps) => {
	const { openModal, isShowing, closeModal } = useModal({});
	const {
		maestrosTramite: { tipoDocumento },
	} = useGlobalContext();

	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const { control, watch, setValue } = useFormContext<Form>();

	const documentGroup = watch(name);
	const documentos = documentGroup.items;
	const documentsAdded = documentos?.length ?? 0;

	const getMatchTipoDocumento = (documentGroup: IDocumentoFormGroup) =>
		tipoDocumento.find(({ id }) => id === documentGroup.tipoDocumento.id);

	const matchTipoDocumento = getMatchTipoDocumento(documentGroup);

	const getMinRequireds = (documentGroup: IDocumentoFormGroup) => {
		const minDocumento = getMatchTipoDocumento(documentGroup)?.minimoObligatorio ?? 0;
		return minDocumento;
	};

	const getCounterText = (documentsAdded: number, minRequired: number) =>
		`Aportados: ${documentsAdded} / ${minRequired}`;

	const onSave = (value: IDocumentoForm) => {
		const newValue = { ...documentGroup };
		newValue.items.push(value);

		setValue(name, newValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
	};

	const onDelete = (data: IDocumentoForm) => {
		const index = findID({ list: documentos, idReact: data.idReact, id: data.id });

		const modalSuccesEliminarDocument = () => {
			openNotification(notificationTemplate.successMessage(
				'Documento eliminado',
				'Se ha eliminado correctamente el documento.'
			));
		};

		const remove = (indexToRemove: number) => {
			const newValue = { ...documentGroup };
			const newsItems = newValue.items.filter((_, index) => index !== indexToRemove);
			newValue.items = newsItems;
			setValue(name, newValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
		};

		openNotification(
			notificationTemplate.removeItem({
				title: 'Eliminar documento',
				content: '¿Estás seguro de eliminar el documento seleccionado?',
				onAccept() {
					setTimeout(() => {
						modalSuccesEliminarDocument();
					}, 300);
					remove(index);
				},
			}),
		);
	};

	return (
		<>
			<Controller
				control={control}
				name={name}
				render={({ field: { value }, fieldState: { error } }) => (
					<EntityGroup
						title={documentGroup.tipoDocumento.descripcion}
						action={{ label: 'Añadir documento', onClick: openModal }}
						hasError={Boolean(error?.message)}
						counterTxt={getCounterText(documentsAdded, getMinRequireds(documentGroup))}
						className={className}
						isShowChildren={value.items.length > 0}
					>
						<DocumentosListado
							documentos={value.items}
							idSolicitud={idSolicitud}
							confirmDeleteDoc={onDelete}
						/>
					</EntityGroup>
				)}
			/>
			<ModalAddDocumento
				title="Añadir documento"
				isOpen={isShowing}
				setIsOpen={closeModal}
				allDocuments={allDocuments}
				availablesDocumentos={matchTipoDocumento ? [matchTipoDocumento] : []}
				onSave={onSave}
			/>
		</>
	);
};

export default DocumentGroup;
